import { ArrowLeftOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Flex, Input, Skeleton, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  useGetComplianceSettingsQuery,
  useSaveComplianceTemplateInstructionsMutation,
} from "../../service/compliance.service";
import styles from "./compliance.module.scss";

const PLACEHOLDER = `Example rules:
- Risk: If the clinician mentions suicidal ideation, document the Columbia Suicide Severity Rating Scale (C-SSRS) score and a safety plan.
- Screening: Document PHQ-9 score at least once every 90 days for patients with a depression diagnosis.
- Coding: CPT code 90837 requires documentation of 53+ minutes of psychotherapy.`;

const ComplianceRuleEdit = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { data, isLoading } = useGetComplianceSettingsQuery();
  const [save, { isLoading: isSaving }] = useSaveComplianceTemplateInstructionsMutation();
  const [draft, setDraft] = useState("");
  const [initial, setInitial] = useState("");

  const numericTemplateId = Number(templateId);
  const template = data?.templates?.find((t) => t.id === numericTemplateId);

  // Seed the textarea once the template loads. Using initial to detect dirty
  // state so the save button only enables on actual edits.
  useEffect(() => {
    if (template) {
      setDraft(template.instructions);
      setInitial(template.instructions);
    }
  }, [template]);

  if (!userInfo?.show_compliance_dashboard || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  if (isLoading) {
    return (
      <div className={styles.compliance__container}>
        <Skeleton active title={{ width: "45%" }} paragraph={{ rows: 1 }} />
        <Skeleton.Input active block style={{ height: 280, marginTop: 24 }} />
      </div>
    );
  }

  if (!template) {
    return (
      <div className={styles.compliance__container}>
        <Flex vertical align="center" gap={12} style={{ padding: 80 }}>
          <Typography.Text style={{ color: "#6b7280" }}>Template not found.</Typography.Text>
          <Button onClick={() => navigate("/compliance/manage-rules")}>Back to manage rules</Button>
        </Flex>
      </div>
    );
  }

  const isDirty = draft !== initial;

  const handleSave = async () => {
    try {
      await save({ noteId: template.id, instructions: draft }).unwrap();
      setInitial(draft);
      message.success(`Saved rules for "${template.name}"`);
    } catch {
      message.error("Failed to save. Please try again.");
    }
  };

  return (
    <div className={styles.compliance__container}>
      <Flex align="center" gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/compliance/manage-rules")}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance Dashboard
                </a>
              ),
            },
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance/manage-rules");
                  }}
                >
                  Manage Rules
                </a>
              ),
            },
            { title: template.name },
          ]}
        />
      </Flex>

      <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap" style={{ marginBottom: 20 }}>
        <Flex vertical gap={4}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {template.name}
          </Typography.Title>
          <Typography.Text style={{ color: "#6b7280", fontSize: 13 }}>
            Plain-English rules the AI checks every note from this template against. One rule per line. Leave blank to
            skip compliance evaluation for this template.
          </Typography.Text>
        </Flex>
        <Button type="primary" disabled={!isDirty} loading={isSaving} onClick={handleSave}>
          Save rules
        </Button>
      </Flex>

      {template.document_preview ? (
        <Card size="small" style={{ marginBottom: 16, background: "#fafafa", borderColor: "#e5e7eb" }}>
          <Flex vertical gap={4}>
            <Typography.Text
              style={{
                fontSize: 11,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontWeight: 600,
              }}
            >
              Template preview
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
              {template.document_preview}
            </Typography.Text>
          </Flex>
        </Card>
      ) : null}

      <Input.TextArea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={PLACEHOLDER}
        autoSize={{ minRows: 16, maxRows: 32 }}
        style={{ fontFamily: "monospace", fontSize: 13 }}
      />
      <Flex justify="flex-end" style={{ marginTop: 16 }}>
        <Button type="primary" disabled={!isDirty} loading={isSaving} onClick={handleSave}>
          Save rules
        </Button>
      </Flex>
    </div>
  );
};

export default ComplianceRuleEdit;
